import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircleIcon, CheckCircle2Icon, PencilIcon } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import {
  ReviewQuestionType,
  type ReviewAnswer,
  type ReviewQuestion,
} from '@gokrew/models/performance';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utility/utility';
import { isExtensionChoice } from '../ReviewFormRenderer/extensionChoice';
import { hasReviewAnswer } from '../ReviewFormRenderer/reviewAnswers';
import type { ReviewStep } from './steps';
import styles from './ReviewFormStepper.module.css';

const DEFAULT_SCALE_MAX = 5;

interface ReviewStepSummaryProps {
  steps: ReviewStep[];
  answers: Map<string, ReviewAnswer>;
  /** Steps that still need an answer, in form order. */
  missing: ReviewStep[];
  /** Opens the question at this step index. */
  onEdit: (index: number) => void;
  /** Said under the title, e.g. that answers are saved as a draft. */
  note?: ReactNode;
}

/**
 * The last step: every answer on one page, read-only, each row opening its
 * question again, and the questions still to answer listed first.
 */
export const ReviewStepSummary = forwardRef<
  HTMLHeadingElement,
  ReviewStepSummaryProps
>(({ steps, answers, missing, onEdit, note }, headingRef) => {
  const { t } = useTranslation(TranslationNS.Performance);
  const missingIds = new Set(missing.map((s) => s.question.id));
  const indexOf = new Map(steps.map((s, i) => [s.question.id, i]));
  const sections = steps.reduce<
    { id: string; title: string; steps: ReviewStep[] }[]
  >((groups, step) => {
    const last = groups[groups.length - 1];
    if (last?.id === step.section.id) last.steps.push(step);
    else
      groups.push({
        id: step.section.id,
        title: step.section.title,
        steps: [step],
      });
    return groups;
  }, []);

  return (
    <div className={styles.summary}>
      <div className={styles.summaryHead}>
        <h2 ref={headingRef} tabIndex={-1} className={styles.summaryTitle}>
          {t('shared.stepper.summaryTitle')}
        </h2>
        <p className={styles.summaryIntro}>
          {t('shared.stepper.summaryIntro')}
        </p>
        {note && <p className={styles.summaryNote}>{note}</p>}
      </div>

      {missing.length > 0 ? (
        <div className={styles.missing}>
          <p className={styles.missingTitle}>
            <AlertCircleIcon aria-hidden />
            {t('shared.stepper.missing', { count: missing.length })}
          </p>
          <ul className={styles.missingList}>
            {missing.map((step) => (
              <li key={step.question.id}>
                <button
                  type="button"
                  className={styles.missingLink}
                  onClick={() => onEdit(indexOf.get(step.question.id) ?? 0)}
                >
                  {step.number}. {step.question.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={styles.complete}>
          <CheckCircle2Icon aria-hidden />
          {t('shared.stepper.complete')}
        </p>
      )}

      {sections.map((section) => (
        <section key={section.id} className={styles.summarySection}>
          <h3 className={styles.summarySectionTitle}>{section.title}</h3>
          <ul className={styles.rows}>
            {section.steps.map((step) => {
              const { question } = step;
              const answer = answers.get(question.id);
              const isMissing = missingIds.has(question.id);
              return (
                <li key={question.id}>
                  <button
                    type="button"
                    data-question-id={question.id}
                    className={cn(styles.row, isMissing && styles.rowMissing)}
                    onClick={() => onEdit(indexOf.get(question.id) ?? 0)}
                  >
                    <span className={styles.rowQuestion}>
                      <span className={styles.rowNumber}>{step.number}.</span>
                      <span>{question.title}</span>
                    </span>
                    <SummaryAnswer question={question} answer={answer} />
                    {isMissing && (
                      <span className={styles.rowRequired}>
                        {t('shared.stepper.requiredMissing')}
                      </span>
                    )}
                    <span className={styles.rowEdit}>
                      <PencilIcon aria-hidden />
                      {t('shared.stepper.edit')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
});

ReviewStepSummary.displayName = 'ReviewStepSummary';

interface SummaryAnswerProps {
  question: ReviewQuestion;
  answer?: ReviewAnswer;
}

/** One answer as a line of text, its rating comment under it. */
const SummaryAnswer = ({ question, answer }: SummaryAnswerProps) => {
  const { t, i18n } = useTranslation(TranslationNS.Performance);
  const comment =
    question.type === ReviewQuestionType.RATING && question.commentEnabled
      ? answer?.text?.trim()
      : undefined;

  const value = (() => {
    if (!hasReviewAnswer(question, answer)) return null;
    switch (question.type) {
      case ReviewQuestionType.RATING: {
        const rating = answer?.rating ?? 0;
        const name = question.scaleLabels?.[rating - 1]?.trim();
        const score = t('shared.stepper.ratingValue', {
          value: rating,
          max: question.scaleMax ?? DEFAULT_SCALE_MAX,
        });
        return name ? `${score} · ${name}` : score;
      }
      case ReviewQuestionType.MULTIPLE_CHOICE: {
        const label =
          question.options?.find((o) => o.id === answer?.optionId)?.label ?? '';
        if (!isExtensionChoice(question, answer)) return label;
        const until = answer?.date
          ? t('shared.form.extendedUntil', {
              date: formatDate(answer.date, {
                format: 'medium',
                locale: i18n.language,
              }),
            })
          : t('shared.form.extendedNoDate');
        return `${label} · ${until}`;
      }
      case ReviewQuestionType.GOALS:
        return t('shared.stepper.goalComments', {
          count: answer?.goals?.filter((g) => !!g.comment?.trim()).length ?? 0,
        });
      default:
        return answer?.text ?? '';
    }
  })();

  return (
    <>
      {value ? (
        <span
          className={cn(
            styles.rowAnswer,
            question.type === ReviewQuestionType.TEXT && styles.rowText
          )}
        >
          {value}
        </span>
      ) : (
        <span className={styles.rowEmpty}>{t('shared.form.noAnswer')}</span>
      )}
      {comment && (
        <span className={styles.rowComment}>
          <span className={styles.rowCommentLabel}>
            {t('shared.form.ratingComment')}
          </span>
          {comment}
        </span>
      )}
    </>
  );
};
